import forge from 'apis/forge';
import { Frame } from 'dragonmouse-shared/interfaces';
import { generateKeyBetween } from 'fractional-indexing';
import { MenuItemAction } from 'menu/MenuItem';
import { selectActiveScene, selectSceneComments, selectUserSettings } from 'redux/db/reducers';
import { canEdit, Direction, getFrame } from 'redux/db/util';
import {
	activateLastTool,
	setActiveTool,
	setPanelsVisible,
	setSettingsModalVisible,
	setToolModified,
} from 'redux/mode/actions';
import { selectTransform, ToolType } from 'redux/mode/reducers';
import store from 'redux/store';
import { TargetCanvas } from 'transfer/Target';
import { hasPrimaryModifier, hasSecondaryModifier } from 'utils';
import { v4 as uuid } from 'uuid';
import {
	alignSelectedFrames,
	getSelectedFrameId,
	getSelectedFrameIds,
	moveSelectedFrames,
} from './frame';
import { displayPromptModal, displayViewOnlyModal } from './modals';
import { getScenePoint, getVisibleCanvasCenterInSceneCoordinates } from './transform';

let registeredEventListeners = false;

export function startListeningForEvents() {
	if (!registeredEventListeners) {
		window.addEventListener('keydown', globalKeydownHandler);
		window.addEventListener('keyup', globalKeyupHandler);

		window.addEventListener('gesturestart', (e) => e.preventDefault());
		window.addEventListener('gesturechange', (e) => e.preventDefault());
		window.addEventListener('gestureend', (e) => e.preventDefault());

		registeredEventListeners = true;
	}
}

enum ElementFocusState {
	FIELD = 'field',
	UNDO_EXEMPT_FIELD = 'undo-exempt-field',
	NON_FIELD = 'non-field',
	OTHER = 'other',
}

const getElementFocusState = (): ElementFocusState => {
	const { activeElement } = document;

	if (activeElement) {
		let actualFocusedElement: Element | null = activeElement;

		if (activeElement.shadowRoot?.activeElement) {
			actualFocusedElement = activeElement.shadowRoot.activeElement;
		}

		if (actualFocusedElement) {
			const tagName = actualFocusedElement.tagName.toLowerCase();
			if (['input', 'textarea'].includes(tagName)) {
				if (
					(actualFocusedElement as HTMLInputElement | HTMLTextAreaElement).dataset.undoExempt ===
					'true'
				) {
					return ElementFocusState.UNDO_EXEMPT_FIELD;
				}
				return ElementFocusState.FIELD;
			}
			if (
				(actualFocusedElement as HTMLElement).isContentEditable ||
				actualFocusedElement.closest('[contenteditable="true"]')
			) {
				return ElementFocusState.FIELD;
			}
			if (['button', 'select'].includes(tagName)) {
				return ElementFocusState.NON_FIELD;
			}
		}
	}

	return ElementFocusState.OTHER;
};

const isAnyModalVisible = (): boolean => {
	const state = store.getState().mode;
	return (
		state.promptModal !== null ||
		state.confirmModal !== null ||
		state.alertModal !== null ||
		state.settingsModalVisible ||
		state.visibleToolModal !== null
	);
};

const globalKeydownHandler = async (event: KeyboardEvent) => {
	const { panelsVisible, selected, canvasMousePosition } = store.getState().mode;
	const selectedFrameId = getSelectedFrameId(selected);
	const scene = selectActiveScene(store.getState().db, store.getState().mode.activeSceneId);
	const elementFocus = getElementFocusState();

	const requireEdit = async (action: () => void | Promise<void>) => {
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		await action();
	};

	switch (event.key) {
		case '-':
		case '_':
			if (event.metaKey || event.ctrlKey) {
				event.preventDefault();
				MenuItemAction.createZoomOut().callback();
				return;
			}
			break;
		case '=':
		case '+':
			if (event.metaKey || event.ctrlKey) {
				event.preventDefault();
				MenuItemAction.createZoomIn().callback();
				return;
			}
			break;
		case '0':
			if (hasPrimaryModifier(event)) {
				event.preventDefault();
				MenuItemAction.createZoomReset().callback();
				return;
			}
			break;
	}

	if (elementFocus == ElementFocusState.FIELD) return;

	switch (event.key) {
		case 'z':
		case 'Z':
			if (hasPrimaryModifier(event)) {
				event.preventDefault();
				if (event.shiftKey) {
					MenuItemAction.createRedo().callback();
				} else {
					MenuItemAction.createUndo().callback();
				}
			} else store.dispatch(setActiveTool(ToolType.ZOOM));
			break;
	}

	if (elementFocus == ElementFocusState.UNDO_EXEMPT_FIELD) return;

	switch (event.key) {
		case 'Escape':
			if (store.getState().mode.activeTool !== ToolType.SELECT) {
				store.dispatch(setActiveTool(ToolType.SELECT));
			}
			break;
	}

	if (elementFocus == ElementFocusState.NON_FIELD) return;

	if (isAnyModalVisible()) return;

	switch (event.key) {
		case '.':
		case '\\':
			if (hasPrimaryModifier(event)) {
				store.dispatch(setPanelsVisible(!panelsVisible));
			}
			break;
		case ',':
			if (hasPrimaryModifier(event)) {
				event.preventDefault();
				store.dispatch(setSettingsModalVisible(true));
			}
			break;
		case ' ':
			store.dispatch(setActiveTool(ToolType.HAND));
			break;
		case '!':
			event.preventDefault();
			MenuItemAction.createZoomToFitCanvas().callback();
			break;
		case '#':
			event.preventDefault();
			MenuItemAction.createZoomToFitSelection().callback();
			break;
		case 'a':
		case 'A':
		case 'å':
			if (hasSecondaryModifier(event) || event.key === 'å') {
				requireEdit(() => alignSelectedFrames('left'));
				break;
			}
			break;
		case 'c':
		case 'C':
			if (hasPrimaryModifier(event)) {
				const selection = window.getSelection();
				if (selection?.toString()) {
					navigator.clipboard.writeText(selection.toString());
				} else {
					const selectedFrameIds = getSelectedFrameIds(selected);

					if (selectedFrameIds.length > 0) {
						if (selectedFrameIds.length === 1) {
							const selectedFrame = getFrame(selectedFrameIds[0]);
							if (selectedFrame) {
								MenuItemAction.createCopyFrameUrl(selectedFrame).callback();
							}
						} else {
							const selectedFrames = selectedFrameIds
								.map((id) => getFrame(id))
								.filter((f): f is Frame => f !== null);
							if (selectedFrames.length > 0) {
								MenuItemAction.createCopyFrameUrl(selectedFrames).callback();
							}
						}
					}
				}
				break;
			}
			// Bare C: add scene comment
			event.preventDefault();
			requireEdit(() => {
				const state = store.getState();
				if (!selectUserSettings(state.db).commentEditingEnabled) return;
				const activeSceneId = state.mode.activeSceneId;
				if (!activeSceneId) return;
				const center = getVisibleCanvasCenterInSceneCoordinates();
				const zoom = selectTransform(state.mode).scale;
				const comments = selectSceneComments(state.db);
				const lastSortOrder =
					comments.length > 0 ? comments[comments.length - 1].metadata?.sortOrder ?? null : null;
				const sortOrder = generateKeyBetween(lastSortOrder, null);
				const chatId = state.mode.activeSceneChatId ?? null;
				const chatContainer = document.querySelector(
					'[data-chat-scroll-container]'
				) as HTMLElement | null;
				const chatScrollTop = chatContainer?.scrollTop;
				const chatScrollHeight = chatContainer?.scrollHeight;
				displayPromptModal({
					title: 'Add Comment',
					submitLabel: 'Add',
					placeholder: 'Enter comment text...',
					isMultilineInput: true,
					disabled: (value: string) => !value.trim(),
					onSubmit: (value?: string) => {
						if (!value?.trim()) return;
						forge.createSceneComment({
							id: uuid(),
							sceneId: activeSceneId,
							x: Math.round(center.x),
							y: Math.round(center.y),
							text: value.trim(),
							metadata: {
								zoom,
								sortOrder,
								chatId,
								chatScrollTop,
								chatScrollHeight,
							},
						});
					},
				});
			});
			break;
		case 'd':
		case 'D':
		case '∂':
			if (hasPrimaryModifier(event)) {
				// duplicate
			}
			if (hasSecondaryModifier(event) || event.key === '∂') {
				requireEdit(() => alignSelectedFrames('right'));
			}
			break;
		case 'p':
		case 'P':
		case 'e':
		case 'E':
			event.preventDefault();
			const frameIdsP = getSelectedFrameIds(selected);
			if (frameIdsP.length === 1) {
				const preview = MenuItemAction.createOpenPrototype(frameIdsP[0]);
				if (preview && !preview.disabled) {
					preview.callback();
				}
			}
			break;
		case 's':
		case 'S':
		case 'ß':
			if (hasSecondaryModifier(event) || event.key === 'ß') {
				requireEdit(() => alignSelectedFrames('bottom'));
				break;
			}
			break;
		case 'v':
		case 'V':
		case '◊':
			if (hasPrimaryModifier(event) || event.key === '◊') {
				// Paste is for pasting frames into the canvas; skip in Design System Editor
				if (store.getState().mode.activeDesignSystemId) {
					return;
				}
				requireEdit(async () => {
					const paste = await MenuItemAction.createPaste(
						new TargetCanvas(getScenePoint(canvasMousePosition))
					);
					if (paste) paste.callback();
				});
			}
			break;
		case 'w':
		case 'W':
		case '∑':
			if (hasSecondaryModifier(event) || event.key === '∑') {
				requireEdit(() => alignSelectedFrames('top'));
			}
			break;
		case 'x':
		case 'X':
			if (hasPrimaryModifier(event)) {
				// cut
			}
			break;
		case 'Alt':
			store.dispatch(setToolModified(true));
			break;
		case 'ArrowRight':
			event.preventDefault();
			if (hasSecondaryModifier(event)) {
				requireEdit(() => moveSelectedFrames({ x: 1, y: 0 }));
			} else if (event.shiftKey) {
				requireEdit(() => moveSelectedFrames({ x: 10, y: 0 }));
			} else if (selectedFrameId) {
				MenuItemAction.createNavFromFrame(selectedFrameId, Direction.RIGHT).callback();
			}
			break;
		case 'ArrowLeft':
			event.preventDefault();
			if (hasSecondaryModifier(event)) {
				requireEdit(() => moveSelectedFrames({ x: -1, y: 0 }));
			} else if (event.shiftKey) {
				requireEdit(() => moveSelectedFrames({ x: -10, y: 0 }));
			} else if (selectedFrameId) {
				MenuItemAction.createNavFromFrame(selectedFrameId, Direction.LEFT).callback();
			}
			break;
		case 'ArrowDown':
			event.preventDefault();
			if (hasSecondaryModifier(event)) {
				requireEdit(() => moveSelectedFrames({ x: 0, y: 1 }));
			} else if (event.shiftKey) {
				requireEdit(() => moveSelectedFrames({ x: 0, y: 10 }));
			} else if (selectedFrameId) {
				MenuItemAction.createNavFromFrame(selectedFrameId, Direction.DOWN).callback();
			}
			break;
		case 'ArrowUp':
			event.preventDefault();
			if (hasSecondaryModifier(event)) {
				requireEdit(() => moveSelectedFrames({ x: 0, y: -1 }));
			} else if (event.shiftKey) {
				requireEdit(() => moveSelectedFrames({ x: 0, y: -10 }));
			} else if (selectedFrameId) {
				MenuItemAction.createNavFromFrame(selectedFrameId, Direction.UP).callback();
			}
			break;
		case 'Backspace':
		case 'Delete': {
			const frameIds = selected.filter((pick) => pick.isFrame()).map((pick) => pick.frameId);
			if (frameIds.length > 0) {
				MenuItemAction.createDeleteFrames(frameIds).callback();
			}
			break;
		}
	}
};

const globalKeyupHandler = (event: KeyboardEvent) => {
	const elementFocus = getElementFocusState();
	if (elementFocus !== ElementFocusState.OTHER) return;

	switch (event.key) {
		case ' ':
			store.dispatch(activateLastTool());
			break;
		case 'z':
		case 'Z':
			if (store.getState().mode.activeTool === ToolType.ZOOM) {
				store.dispatch(activateLastTool());
			}
			break;
		case 'Alt':
			store.dispatch(setToolModified(false));
			break;
	}
};
